"use client";

import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HelpCircle,
  Search,
  Shield,
  IndianRupee,
  Users,
  Smartphone,
  BookOpen,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ERPLeadBot from "./ERPLeadBot";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Footer from "./Footer";

const FAQ_DATA = [
  {
    id: 1,
    category: "general",
    question: "What is Clynox?",
    answer:
      "Clynox is an all-in-one management platform for schools that brings admissions, fees, attendance, academics, transport and inventory into a single system, replacing scattered spreadsheets and registers with one connected dashboard.",
    tags: ["overview", "school management", "erp"],
  },
  {
    id: 2,
    category: "general",
    question: "Who is Clynox built for?",
    answer:
      "Clynox is built for schools of all sizes across India, from single-campus institutions to multi-branch school groups, as well as their transport and inventory teams who need a simple, reliable system for day-to-day operations.",
    tags: ["schools", "use case"],
  },
  {
    id: 3,
    category: "general",
    question: "What modules does Clynox include?",
    answer:
      "Clynox covers student records, admissions, attendance, fee management, exams and report cards, staff management, parent communication, bus and transport tracking, and inventory management. You can start with the modules you need and add more as your school grows.",
    tags: ["features", "modules"],
  },
  {
    id: 4,
    category: "general",
    question: "Is Clynox difficult for teachers and staff to use?",
    answer:
      "No. Clynox is designed with a simple, intuitive interface so non-technical staff can get comfortable within a few days. We also provide guided onboarding and training to help your team get started quickly.",
    tags: ["ease of use", "training"],
  },
  {
    id: 5,
    category: "general",
    question: "Can Clynox be customised for our school's processes?",
    answer:
      "Yes. Fee structures, academic calendars, attendance rules, transport routes and report formats can all be configured to match how your school already operates, so you don't have to change your processes to fit the software.",
    tags: ["customisation", "configuration"],
  },
  {
    id: 6,
    category: "pricing",
    question: "How is Clynox priced?",
    answer:
      "Clynox is typically priced on a per-student, per-month basis, so smaller schools pay less and larger schools pay according to usage. The exact pricing depends on the modules you choose and the number of students. Reach out to us for a tailored quote.",
    tags: ["pricing", "cost"],
  },
  {
    id: 7,
    category: "pricing",
    question: "Are there any hidden charges or setup fees?",
    answer:
      "No. Any one-time setup or onboarding fee, if applicable, is shared upfront in the proposal. There are no hidden costs, your subscription covers the modules and support included in your plan.",
    tags: ["pricing", "transparency"],
  },
  {
    id: 8,
    category: "pricing",
    question: "Do you offer a free trial or demo?",
    answer:
      "Yes. We offer a guided demo where our team walks you through the key modules, including fees, attendance, transport and communication, using sample data so you can see exactly how Clynox would work for your school.",
    tags: ["demo", "trial"],
  },
  {
    id: 9,
    category: "pricing",
    question: "How long does it take to set up Clynox for our school?",
    answer:
      "Most schools go live within 2-4 weeks. The timeline depends on how many modules you choose and how quickly student, staff and fee data can be shared with us for migration.",
    tags: ["onboarding", "timeline"],
  },
  {
    id: 10,
    category: "security",
    question: "How secure is our school's data on Clynox?",
    answer:
      "Clynox runs on secure cloud infrastructure with encrypted data, role-based access control and activity logs, so only authorised staff can view or edit specific records, and every action is traceable.",
    tags: ["security", "data protection"],
  },
  {
    id: 11,
    category: "security",
    question: "Who owns our school's data, and can we export it?",
    answer:
      "Your school always owns its data. It is stored on secure servers with regular backups, and if you ever need to move to another system, we can help you export your records in a usable format.",
    tags: ["data ownership", "backups"],
  },
  {
    id: 12,
    category: "security",
    question: "How are student and parent records kept private?",
    answer:
      "Access to student and parent information is restricted by role. For example, teachers only see data for their own classes, while accountants only see fee-related information, keeping sensitive data visible only to those who need it.",
    tags: ["privacy", "access control"],
  },
  {
    id: 13,
    category: "support",
    question: "What training do you provide for our staff?",
    answer:
      "We provide role-based training for admins, accountants, teachers and transport staff, including live sessions, recorded walkthroughs and quick-reference guides, so every team member knows how to use the features relevant to them.",
    tags: ["training", "support"],
  },
  {
    id: 14,
    category: "support",
    question: "What support do we get after going live?",
    answer:
      "After go-live, you get ongoing access to our support team for questions, configuration changes and troubleshooting, along with updates as new features are released.",
    tags: ["support", "after sales"],
  },
  {
    id: 15,
    category: "support",
    question: "Can Clynox integrate with accounting tools like Tally?",
    answer:
      "Yes. Fee and finance data from Clynox can be exported or integrated with accounting software such as Tally, so your accounts team doesn't need to enter the same data twice.",
    tags: ["integration", "tally", "accounting"],
  },
  {
    id: 16,
    category: "mobile",
    question: "Is there a mobile app for parents and teachers?",
    answer:
      "Yes. Parents can use the Clynox app to check fees, attendance, homework and announcements, while teachers can mark attendance, share homework and view their schedule directly from their phones.",
    tags: ["mobile app", "parents", "teachers"],
  },
  {
    id: 17,
    category: "mobile",
    question: "Does Clynox work well on slow or unstable internet?",
    answer:
      "Clynox is optimised for typical Indian network conditions and works on modest bandwidth. As it's a cloud-based system, a basic stable connection is needed for real-time updates and online payments.",
    tags: ["internet", "performance"],
  },
  {
    id: 18,
    category: "mobile",
    question: "Can our transport team track buses using the app?",
    answer:
      "Yes. Clynox includes live bus tracking so transport staff and parents can see vehicle locations, routes and estimated arrival times directly from the mobile app.",
    tags: ["transport", "bus tracking", "mobile app"],
  },
];

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", label: "All Questions", icon: <HelpCircle className="h-4 w-4" /> },
    { id: "general", label: "General", icon: <BookOpen className="h-4 w-4" /> },
    { id: "pricing", label: "Pricing", icon: <IndianRupee className="h-4 w-4" /> },
    { id: "security", label: "Security", icon: <Shield className="h-4 w-4" /> },
    { id: "support", label: "Support", icon: <Users className="h-4 w-4" /> },
    { id: "mobile", label: "Mobile App", icon: <Smartphone className="h-4 w-4" /> },
  ];

  const filteredFaqs = useMemo(() => {
    const s = searchQuery.toLowerCase().trim();
    return FAQ_DATA.filter((faq) => {
      const q = (faq.question || "").toLowerCase();
      const a = (faq.answer || "").toLowerCase();
      const tags = Array.isArray(faq.tags) ? faq.tags : [];
      const matchesSearch =
        !s ||
        q.includes(s) ||
        a.includes(s) ||
        tags.some((tag) => (tag || "").toLowerCase().includes(s));
      const matchesCategory = selectedCategory === "all" || faq.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const animationVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <>
    <section id="faq" className="py-16 sm:py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-100 rounded-full opacity-20 animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={animationVariants}>
            <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200 px-4 py-2 text-sm">
              💬 FREQUENTLY ASKED QUESTIONS
            </Badge>
          </motion.div>
          
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            variants={animationVariants}
          >
            Got Questions?{" "}
            <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              We've Got Answers
            </span>
          </motion.h2>
          
          <motion.p
            className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            variants={animationVariants}
          >
            Find answers to the most frequently asked questions about Clynox school management system
          </motion.p>

          {/* Search Bar */}
          <motion.div
            className="max-w-md mx-auto relative"
            variants={animationVariants}
          >
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 text-lg border-2 border-gray-200 focus:border-blue-500 rounded-xl"
            />
          </motion.div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-12"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedCategory === category.id
                  ? "bg-blue-600 text-white shadow-lg"
                  : "bg-white text-gray-600 hover:bg-blue-50 border border-gray-200"
              }`}
              variants={scaleIn}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.icon}
              {category.label}
            </motion.button>
          ))}
        </motion.div>

        {/* FAQ Grid */}
        <motion.div
          className="max-w-6xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AnimatePresence>
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  variants={animationVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  transition={{ delay: index * 0.1 }}
                >
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value={`item-${faq.id}`} className="border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                      <AccordionTrigger className="px-6 py-4 hover:bg-blue-50 text-left font-medium text-gray-900 group">
                        <div className="flex items-start gap-4 w-full">
                          <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                            <HelpCircle className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="text-base sm:text-lg font-semibold">{faq.question}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                {categories.find(cat => cat.id === faq.category)?.label}
                              </Badge>
                              {(Number(faq.helpful) || 0) > 0 ? (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" />
                                  {Number(faq.helpful)} found helpful
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4 pt-2">
                        <div className="text-gray-600 leading-relaxed">{faq.answer}</div>
                        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                            <span>Tags:</span>
                            {faq.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="px-2 py-1 bg-gray-100 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>

      </div>
    </section>
    <Footer />
    <ERPLeadBot />
    </>
  );
};

export default FAQ;
